import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { isImagePath } from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { CanvasStore } from './store.js';
import { NodeView } from './NodeView.jsx';
import { EdgeLayer } from './edges.jsx';
import { EdgeChrome, NodeChrome } from './chrome.jsx';
import { CanvasToolbar } from './Toolbar.jsx';
import { makeTapDetector, startDrag } from './drag.js';
import {
  clampScale,
  newCanvasId,
  nearestSide,
  nodeAtPoint,
  nodesBBox,
  snap,
  type Pt,
  type Side,
  type TempEdge,
  type Viewport,
} from './model.js';
import './canvas.css';

/**
 * CanvasView: render + edit JSON Canvas 1.0 (.canvas files; spec
 * jsoncanvas.org): text nodes (markdown), file nodes (note/image preview),
 * link nodes, groups; edges with labels/arrows. Pan/zoom (pointer + touch),
 * drag nodes, create/delete nodes and edges, edit text nodes inline.
 * Persistence: the canvas doc's Y.Text holds the canvas JSON; read on mount,
 * write-through (debounced) on edit — last-write-wins is acceptable here.
 */
export interface CanvasViewProps {
  canvasId: string;
  onOpenNote: (id: string) => void;
}

type Selection = { kind: 'node' | 'edge'; id: string } | null;

const emptySubscribe = () => () => {};
const zeroVersion = () => 0;

export function CanvasView(props: CanvasViewProps) {
  const { canvasId, onOpenNote } = props;

  // -- store binding (canvas doc may still be loading from IDB/socket) -------
  const [store, setStore] = useState<CanvasStore | null>(null);
  useEffect(() => {
    let created: CanvasStore | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    const tryBind = (): boolean => {
      const yt = vault.ytext(canvasId);
      if (!yt) return false;
      created = new CanvasStore(yt);
      setStore(created);
      return true;
    };
    if (!tryBind()) {
      timer = setInterval(() => {
        if (tryBind() && timer) {
          clearInterval(timer);
          timer = null;
        }
      }, 250);
    }
    return () => {
      if (timer) clearInterval(timer);
      created?.destroy();
      setStore(null);
    };
  }, [canvasId]);

  const version = useSyncExternalStore(
    store ? store.subscribe : emptySubscribe,
    store ? store.getVersion : zeroVersion,
  );

  // -- viewport --------------------------------------------------------------
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  const [paneSize, setPaneSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const update = () => setPaneSize({ width: el.clientWidth, height: el.clientHeight });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  const clientToWorld = useCallback((cx: number, cy: number): Pt => {
    const rect = paneRef.current?.getBoundingClientRect();
    const v = viewportRef.current;
    const px = cx - (rect?.left ?? 0);
    const py = cy - (rect?.top ?? 0);
    return { x: (px - v.x) / v.scale, y: (py - v.y) / v.scale };
  }, []);

  const zoomToFit = useCallback(() => {
    const el = paneRef.current;
    if (!el || !store) return;
    const pw = el.clientWidth;
    const ph = el.clientHeight;
    const bb = nodesBBox(store.model.nodes);
    if (!bb) {
      setViewport({ x: pw / 2, y: ph / 2, scale: 1 });
      return;
    }
    const pad = 60;
    const scale = clampScale(
      Math.min(
        (pw - pad * 2) / Math.max(bb.width, 1),
        (ph - pad * 2) / Math.max(bb.height, 1),
        1.25,
      ),
    );
    setViewport({
      scale,
      x: pw / 2 - (bb.x + bb.width / 2) * scale,
      y: ph / 2 - (bb.y + bb.height / 2) * scale,
    });
  }, [store]);

  // Zoom-to-fit once per canvas, when the doc has loaded.
  const fittedRef = useRef<string | null>(null);
  useEffect(() => {
    if (store && fittedRef.current !== canvasId) {
      fittedRef.current = canvasId;
      zoomToFit();
    }
  }, [store, canvasId, zoomToFit]);

  // Wheel: plain scroll pans, ctrl/cmd-wheel (and trackbar pinch) zooms at
  // the cursor. Native non-passive listener so preventDefault works.
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Let inner scrollables (md-note previews) scroll normally.
      let t = e.target as HTMLElement | null;
      while (t && t !== el) {
        if (
          !e.ctrlKey &&
          !e.metaKey &&
          t.classList.contains('cv-scroll') &&
          t.scrollHeight > t.clientHeight
        ) {
          return;
        }
        t = t.parentElement;
      }
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      setViewport((v) => {
        if (e.ctrlKey || e.metaKey) {
          const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.01));
          const scale = clampScale(v.scale * factor);
          return {
            scale,
            x: px - ((px - v.x) / v.scale) * scale,
            y: py - ((py - v.y) / v.scale) * scale,
          };
        }
        return { ...v, x: v.x - e.deltaX, y: v.y - e.deltaY };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // -- selection / editing / temp edge --------------------------------------
  const [selection, setSelection] = useState<Selection>(null);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingRef = useRef(editingId);
  editingRef.current = editingId;
  const [tempEdge, setTempEdge] = useState<TempEdge | null>(null);

  const deleteNode = useCallback(
    (id: string) => {
      store?.apply((m) => {
        m.nodes = m.nodes.filter((n) => n.id !== id);
        m.edges = m.edges.filter((e) => e.fromNode !== id && e.toNode !== id);
      });
      setSelection(null);
    },
    [store],
  );

  const deleteEdge = useCallback(
    (id: string) => {
      store?.apply((m) => {
        m.edges = m.edges.filter((e) => e.id !== id);
      });
      setSelection(null);
    },
    [store],
  );

  const editEdgeLabel = useCallback(
    (edgeId: string) => {
      if (!store) return;
      const edge = store.model.edges.find((e) => e.id === edgeId);
      if (!edge) return;
      const label = window.prompt('Edge label', edge.label ?? '');
      if (label === null) return;
      store.apply((m) => {
        const ed = m.edges.find((x) => x.id === edgeId);
        if (ed) ed.label = label.trim() ? label : undefined;
      });
    },
    [store],
  );

  // Delete/Backspace removes the selection; Escape clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Escape') {
        setSelection(null);
        return;
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const sel = selectionRef.current;
      if (!sel || editingRef.current) return;
      e.preventDefault();
      if (sel.kind === 'node') deleteNode(sel.id);
      else deleteEdge(sel.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteNode, deleteEdge]);

  // -- node creation ---------------------------------------------------------
  const centerWorld = useCallback((): Pt => {
    const el = paneRef.current;
    const v = viewportRef.current;
    if (!el) return { x: 0, y: 0 };
    return { x: (el.clientWidth / 2 - v.x) / v.scale, y: (el.clientHeight / 2 - v.y) / v.scale };
  }, []);

  const createTextAt = useCallback(
    (pt: Pt, edit = true) => {
      if (!store) return;
      const id = newCanvasId();
      store.apply((m) => {
        m.nodes.push({
          id,
          type: 'text',
          x: snap(pt.x - 125),
          y: snap(pt.y - 30),
          width: 250,
          height: 60,
          text: '',
          raw: {},
        });
      });
      setSelection({ kind: 'node', id });
      if (edit) setEditingId(id);
    },
    [store],
  );

  const addNote = useCallback(
    (path: string) => {
      if (!store) return;
      const c = centerWorld();
      const img = isImagePath(path);
      const w = img ? 320 : 360;
      const h = img ? 240 : 320;
      const id = newCanvasId();
      store.apply((m) => {
        m.nodes.push({
          id,
          type: 'file',
          file: path,
          x: snap(c.x - w / 2),
          y: snap(c.y - h / 2),
          width: w,
          height: h,
          raw: {},
        });
      });
      setSelection({ kind: 'node', id });
    },
    [store, centerWorld],
  );

  const addLink = useCallback(() => {
    if (!store) return;
    let url = window.prompt('Link URL', 'https://')?.trim();
    if (!url || url === 'https://') return;
    if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) url = `https://${url}`;
    const c = centerWorld();
    const id = newCanvasId();
    store.apply((m) => {
      m.nodes.push({
        id,
        type: 'link',
        url,
        x: snap(c.x - 180),
        y: snap(c.y - 40),
        width: 360,
        height: 80,
        raw: {},
      });
    });
    setSelection({ kind: 'node', id });
  }, [store, centerWorld]);

  const addGroup = useCallback(() => {
    if (!store) return;
    const c = centerWorld();
    const id = newCanvasId();
    store.apply((m) => {
      m.nodes.push({
        id,
        type: 'group',
        label: 'Group',
        x: snap(c.x - 200),
        y: snap(c.y - 150),
        width: 400,
        height: 300,
        raw: {},
      });
    });
    setSelection({ kind: 'node', id });
  }, [store, centerWorld]);

  // -- edge creation (drag from a side ○ of the selected node) ---------------
  const startEdgeDrag = useCallback(
    (nodeId: string, side: Side, e: ReactPointerEvent) => {
      if (!store) return;
      store.beginInteraction();
      setTempEdge({ fromNode: nodeId, fromSide: side, to: clientToWorld(e.clientX, e.clientY) });
      startDrag(e, {
        move: (ev) =>
          setTempEdge((t) => (t ? { ...t, to: clientToWorld(ev.clientX, ev.clientY) } : t)),
        up: (ev, moved) => {
          store.endInteraction();
          setTempEdge(null);
          if (!moved) return;
          const pt = clientToWorld(ev.clientX, ev.clientY);
          const target = nodeAtPoint(store.model.nodes, pt, nodeId);
          if (!target) return;
          const id = newCanvasId();
          const toSide = nearestSide(target, pt);
          store.apply((m) => {
            m.edges.push({ id, fromNode: nodeId, fromSide: side, toNode: target.id, toSide, raw: {} });
          });
          setSelection({ kind: 'edge', id });
        },
      });
    },
    [store, clientToWorld],
  );

  // -- background pan / pinch / tap ------------------------------------------
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<
    | { type: 'pan'; startClient: Pt; startVp: Viewport; moved: boolean }
    | { type: 'pinch'; startDist: number; startMid: Pt; startVp: Viewport }
    | null
  >(null);
  const paneRectRef = useRef<DOMRect | null>(null);
  const bgTapRef = useRef(makeTapDetector());

  const onPanePointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1) return;
    const pane = paneRef.current;
    if (!pane) return;
    try {
      pane.setPointerCapture(e.pointerId);
    } catch {
      /* pointer already gone */
    }
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    paneRectRef.current = pane.getBoundingClientRect();
    const pts = [...pointersRef.current.values()];
    if (pts.length === 2) {
      const rect = paneRectRef.current;
      gestureRef.current = {
        type: 'pinch',
        startDist: Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y) || 1,
        startMid: {
          x: (pts[0].x + pts[1].x) / 2 - rect.left,
          y: (pts[0].y + pts[1].y) / 2 - rect.top,
        },
        startVp: viewportRef.current,
      };
    } else if (pts.length === 1) {
      gestureRef.current = {
        type: 'pan',
        startClient: { x: e.clientX, y: e.clientY },
        startVp: viewportRef.current,
        moved: false,
      };
    }
  };

  const onPanePointerMove = (e: ReactPointerEvent) => {
    const p = pointersRef.current.get(e.pointerId);
    if (p) {
      p.x = e.clientX;
      p.y = e.clientY;
    }
    const g = gestureRef.current;
    if (!g) return;
    if (g.type === 'pan' && pointersRef.current.size === 1) {
      const dx = e.clientX - g.startClient.x;
      const dy = e.clientY - g.startClient.y;
      if (!g.moved && Math.abs(dx) + Math.abs(dy) > 4) g.moved = true;
      if (g.moved) setViewport({ x: g.startVp.x + dx, y: g.startVp.y + dy, scale: g.startVp.scale });
    } else if (g.type === 'pinch' && pointersRef.current.size >= 2) {
      const pts = [...pointersRef.current.values()].slice(0, 2);
      const rect = paneRectRef.current;
      if (!rect) return;
      const mid = {
        x: (pts[0].x + pts[1].x) / 2 - rect.left,
        y: (pts[0].y + pts[1].y) / 2 - rect.top,
      };
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y) || 1;
      const scale = clampScale(g.startVp.scale * (dist / g.startDist));
      const w = {
        x: (g.startMid.x - g.startVp.x) / g.startVp.scale,
        y: (g.startMid.y - g.startVp.y) / g.startVp.scale,
      };
      setViewport({ scale, x: mid.x - w.x * scale, y: mid.y - w.y * scale });
    }
  };

  const onPanePointerUp = (e: ReactPointerEvent) => {
    pointersRef.current.delete(e.pointerId);
    const g = gestureRef.current;
    if (!g) return;
    if (g.type === 'pinch') {
      if (pointersRef.current.size === 1) {
        const rest = [...pointersRef.current.values()][0];
        gestureRef.current = {
          type: 'pan',
          startClient: { x: rest.x, y: rest.y },
          startVp: viewportRef.current,
          moved: true, // was pinching: never treat the tail as a tap
        };
      } else if (pointersRef.current.size === 0) {
        gestureRef.current = null;
      }
      return;
    }
    // pan gesture ending
    gestureRef.current = null;
    if (g.moved || e.type === 'pointercancel') return;
    // Tap on the background: clear selection; double-tap creates a text card.
    setSelection(null);
    setEditingId(null);
    if (bgTapRef.current(e.clientX, e.clientY) && store) {
      createTextAt(clientToWorld(e.clientX, e.clientY));
    }
  };

  // -- render ----------------------------------------------------------------
  const model = store?.model ?? null;
  void version; // version subscription drives re-renders of the mutable model

  const sortedNodes = model
    ? [
        ...model.nodes
          .filter((n) => n.type === 'group')
          .sort((a, b) => b.width * b.height - a.width * a.height),
        ...model.nodes.filter((n) => n.type !== 'group'),
      ]
    : [];

  const selectedNode =
    model && selection?.kind === 'node'
      ? (model.nodes.find((n) => n.id === selection.id) ?? null)
      : null;
  const selectedEdge =
    model && selection?.kind === 'edge'
      ? (model.edges.find((e) => e.id === selection.id) ?? null)
      : null;

  const entry = vault.getEntry(canvasId);
  const getScale = useCallback(() => viewportRef.current.scale, []);

  return (
    <div
      ref={paneRef}
      className="cv-pane"
      style={{
        backgroundPosition: `${viewport.x}px ${viewport.y}px`,
        backgroundSize: `${24 * viewport.scale}px ${24 * viewport.scale}px`,
        backgroundImage: viewport.scale < 0.3 ? 'none' : undefined,
      }}
      onPointerDown={onPanePointerDown}
      onPointerMove={onPanePointerMove}
      onPointerUp={onPanePointerUp}
      onPointerCancel={onPanePointerUp}
    >
      <svg className="cv-edges">
        <g transform={`translate(${viewport.x} ${viewport.y}) scale(${viewport.scale})`}>
          {model && (
            <EdgeLayer
              model={model}
              scale={viewport.scale}
              selectedEdgeId={selectedEdge?.id ?? null}
              onSelectEdge={(id) => setSelection({ kind: 'edge', id })}
              onEditLabel={editEdgeLabel}
              tempEdge={tempEdge}
            />
          )}
        </g>
      </svg>

      <div
        className="cv-world"
        style={{ transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})` }}
      >
        {store &&
          sortedNodes.map((n) => (
            <NodeView
              key={n.id}
              node={n}
              store={store}
              selected={selection?.kind === 'node' && selection.id === n.id}
              editing={editingId === n.id}
              getScale={getScale}
              onSelect={(id) => setSelection({ kind: 'node', id })}
              onEditNode={setEditingId}
              onOpenNote={onOpenNote}
            />
          ))}
      </div>

      {store && selectedNode && editingId !== selectedNode.id && (
        <NodeChrome
          node={selectedNode}
          viewport={viewport}
          paneSize={paneSize}
          store={store}
          onDelete={() => deleteNode(selectedNode.id)}
          onEdgeStart={startEdgeDrag}
        />
      )}
      {store && selectedEdge && (
        <EdgeChrome
          edge={selectedEdge}
          model={store.model}
          viewport={viewport}
          paneSize={paneSize}
          store={store}
          onDelete={() => deleteEdge(selectedEdge.id)}
          onEditLabel={editEdgeLabel}
        />
      )}

      <CanvasToolbar
        onAddText={() => createTextAt(centerWorld())}
        onAddNote={addNote}
        onAddLink={addLink}
        onAddGroup={addGroup}
        onZoomFit={zoomToFit}
      />

      {model && model.nodes.length === 0 && (
        <div className="cv-empty muted">Double-click to add a card</div>
      )}
      {!store &&
        (entry && !entry.deleted ? (
          <div className="spinner" />
        ) : (
          <div className="cv-empty muted">Canvas not found.</div>
        ))}
    </div>
  );
}
